import { useState, useEffect } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import SuccessIcon from '@mui/icons-material/CheckCircle'
import Delete from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion } from '@mui/material'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { object, array, bool, func } from 'prop-types'
import { useDispatch } from 'react-redux'
import { getImageUrl } from '@utils/getImageUrl'
import { EditTemplateContext } from '../edit-template-context'
import * as TemplateActions from '../template.actions'
import { AAValidation } from './validations/aa-validation'
import { BaseValidation } from './validations/base-validation'
import { FBValidation } from './validations/fb-validation'
import { GAValidation } from './validations/ga-validation'

const specialValidation = {
  'adobe-analytics': AAValidation,
  'google-analytics': GAValidation,
  facebook: FBValidation,
}

const useStyles = makeStyles(theme => ({
  error: {
    color: theme.palette.error.main,
  },
  success: {
    color: theme.palette.success.main,
  },
}))

const LinkSuccess = styled(SuccessIcon)`
  margin-left: 10px;
`

const LinkError = styled(ErrorOutlineIcon)`
  margin-left: 10px;
`

const paddingTop = css`
  padding-top: 4px;
`

const fullLength = css`
  margin-top: 12px;
  width: 100%;
`

const flex = css`
  display: flex;
`

export const Validation = ({
  accounts = [],
  type = {},
  validations = [],
  isOpen = false,
  isConfigured = false,
  resetIsOpen = false,
}) => {
  const dispatch = useDispatch()
  const [expanded, setExpanded] = useState(isOpen)

  const classes = useStyles()

  useEffect(() => {
    isOpen && setExpanded(true)
  }, [isOpen])

  const editValidation = values => {
    dispatch(TemplateActions.updateValidation(values))
  }

  const SpecialValidation = specialValidation[type.slug] || BaseValidation

  return (
    <EditTemplateContext.Consumer>
      {() => (
        <Accordion
          expanded={expanded}
          onChange={() => {
            resetIsOpen()
            setExpanded(!expanded)
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            css={paddingTop}
          >
            <Grid container>
              <Grid item xs={4} css={flex}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    marginRight: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img style={{ maxWidth: '100%' }} src={getImageUrl(type.slug)} alt={type.name} />
                </div>
                <Typography>{type.name}</Typography>
                {type.state && type.state !== 'released' ? (
                  <Chip
                    color="secondary"
                    size="small"
                    style={{ marginLeft: 8 }}
                    label={type.state}
                  />
                ) : null}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" css={paddingTop}>
                  {type.description}
                </Typography>
              </Grid>
              <Grid>
                {isConfigured ? (
                  <LinkSuccess className={classes.success} />
                ) : (
                  <LinkError className={classes.error} />
                )}
              </Grid>
            </Grid>
            <IconButton
              style={{
                color: 'black',
              }}
              onClick={e => {
                e.stopPropagation()
                dispatch(TemplateActions.removeValidation(type))
              }}
              aria-label="delete-integration"
              component="span"
              size="small"
            >
              <Delete />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ width: '100%' }}>
              <div style={{ width: '100%' }} css={fullLength}>
                <SpecialValidation
                  accounts={accounts}
                  type={validations.filter(val => val.slug == type.slug)[0]}
                  validation={type}
                  onChange={editValidation}
                />
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      )}
    </EditTemplateContext.Consumer>
  )
}

Validation.propTypes = {
  type: object,
  accounts: array,
  validations: array,
  isOpen: bool,
  isConfigured: bool,
  resetIsOpen: func,
}
